import classNames from 'classnames';
import { type FC, useCallback, useEffect, useRef } from 'react';

import { getFallbackImgUrl } from '../../utils/cloudinaryImageUtils';

interface ImageProps {
    src: string;
    srcSet?: string;
    fallbackSrc?: string;

    alt?: string;
    title?: string;
    cssClass?: string;
    wrapperClassName?: string;
    lazy?: boolean;
    width?: string | number;
    height?: string | number;
    style?: React.CSSProperties;
    ariaLabel?: string;
    tabIndex?: number;
}

const Image: FC<ImageProps> = (props: ImageProps) => {
    const fallbackSrc = getFallbackImgUrl(props.fallbackSrc);

    // we operate on ref to not cause additional rerenders with useStates
    const imgRef = useRef<HTMLImageElement>(null);
    const hasErrored = useRef(false);

    useEffect(() => {
        hasErrored.current = false;
    }, [props.src, props.srcSet]);

    const applyFallbackImage = useCallback((fallbackUrl: string) => {
        if (imgRef.current) {
            imgRef.current.src = fallbackUrl;
            imgRef.current.removeAttribute('srcset');
        }
    }, []);

    useEffect(() => {
        if (imgRef.current && hasErrored.current) {
            applyFallbackImage(fallbackSrc);
        }
    }, [applyFallbackImage, fallbackSrc]);

    const handleError = () => {
        if (imgRef.current) {
            hasErrored.current = true;
            applyFallbackImage(fallbackSrc);
        }
    };

    if (window.isPrerenderCrawler) {
        return (
            <div className={classNames('u-heightAll', props.wrapperClassName)}>
                <img
                    ref={imgRef}
                    style={props.style}
                    className={props.cssClass}
                    src={props.src}
                    srcSet={props.srcSet}
                    alt={props.alt || ''}
                    draggable="false"
                    onError={handleError}
                    aria-label={props.ariaLabel}
                />
            </div>
        );
    }

    return (
        <img
            ref={imgRef}
            tabIndex={props.tabIndex}
            alt={props.alt || ''}
            title={props.title}
            src={props.src}
            srcSet={props.srcSet}
            draggable="false"
            className={props.cssClass}
            width={props.width}
            height={props.height}
            loading={props.lazy ? 'lazy' : undefined}
            style={props.style}
            onError={handleError}
        />
    );
};

export default Image;
